/**
 * Enhanced Translation Hooks for XOFFI
 * Provides easy-to-use hooks for static and dynamic translations
 */

import { useTranslation as useI18nextTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEnhancedLanguage } from '@/providers/enhanced-i18n-provider';
import { BackendAuth } from '@/lib/auth-backend';

/**
 * Deep merge utility for nested objects
 */
function deepMerge(target, source) {
  const output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Enhanced useTranslation hook with backend integration
 */
export function useTranslation(namespace = 'translation') {
  const { t: i18nT, i18n, ...rest } = useI18nextTranslation(namespace);
  const { translateDynamic, isBackendAvailable, translationsVersion: providerVersion, isProviderReady } = useEnhancedLanguage();
  const fetchedKeysRef = useRef(new Set());
  const [translationVersion, setTranslationVersion] = useState(0);

  // 🔥 CRITICAL: Sync with provider's translationsVersion to trigger re-renders
  useEffect(() => {
    // console.log(`🔍 [useTranslation] useEffect triggered! providerVersion: ${providerVersion}, namespace: ${namespace}`);

    if (providerVersion !== undefined) {
      // console.log(`🔄 [useTranslation] Provider translations version changed to ${providerVersion}, updating local version...`);
      setTranslationVersion(prev => prev + 1);
    } else {
      // console.log(`⚠️ [useTranslation] providerVersion is undefined - skipping local version update`);
    }
  }, [providerVersion, namespace]);

  // Async function to fetch missing translation with pending-state polling
  const fetchMissingTranslation = useCallback(async (key, language, ns, retryCount = 0) => {
    // 🛑 BLOCK: Skip if key was batch-loaded (prevents redundant individual requests!)
    if (typeof window !== 'undefined' && window.__batchLoadedTranslationKeys?.has(key)) {
      // console.log(`🛑 [useTranslation] Skipping individual fetch for batch-loaded key: ${key}`);
      return;
    }

    const attemptKey = `${language}::${ns}::${key}`;
    if (fetchedKeysRef.current.has(attemptKey)) {
      return;
    }
    fetchedKeysRef.current.add(attemptKey);
    try {
      const currentPath = (typeof window !== 'undefined' && window.location?.pathname) || null;
      const user = BackendAuth.getUser();
      // ALWAYS use organization_id = 1 for translations (global standard)
      const organizationId = 1; // NEVER use user organisation for translations!

      const response = await fetch(
        `/api/translations/key/${encodeURIComponent(key)}?language=${language}&namespace=${ns}&organization_id=${organizationId}`,
        {
          headers: {
            ...(currentPath ? { 'X-Translation-Page': currentPath } : {}),
            'X-Organisation': '1', // Always use global organisation for translations
            'X-User-Id': String(user?.id || ''),
            'X-Menu-Group': String(user?.menu_group_id || ''),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Check if translation is pending (being translated by backend)
        if (data.success && data.status === 'pending') {
          // console.log(`⏳ Translation pending for "${key}" (${language}), will retry in ${data.estimated_time || 3}s...`);

          // Show loading indicator by adding temporary placeholder (localized)
          const loadingTexts = {
            de: '⟳ Lädt...',
            en: '⟳ Loading...',
            fr: '⟳ Chargement...',
            es: '⟳ Cargando...',
            it: '⟳ Caricamento...',
            pt: '⟳ Carregando...',
            nl: '⟳ Laden...',
            pl: '⟳ Ładowanie...',
            ru: '⟳ Загрузка...',
            tr: '⟳ Yükleniyor...',
            ar: '⟳ جار التحميل...',
            zh: '⟳ 加载中...',
            ja: '⟳ 読み込み中...'
          };
          const loadingText = loadingTexts[language] || loadingTexts['en'];
          const keyParts = key.split('.');
          if (keyParts.length === 1) {
            i18n.addResource(language, ns, key, loadingText);
          } else {
            const nestedObj = {};
            let current = nestedObj;
            for (let i = 0; i < keyParts.length - 1; i++) {
              current[keyParts[i]] = {};
              current = current[keyParts[i]];
            }
            current[keyParts[keyParts.length - 1]] = loadingText;
            const existingBundle = i18n.getResourceBundle(language, ns) || {};
            const merged = deepMerge(existingBundle, nestedObj);
            i18n.addResourceBundle(language, ns, merged, true, true);
          }
          setTranslationVersion(v => v + 1);

          // Auto-retry after estimated time (max 20 retries = ~60 seconds)
          // Increased to handle large batch translations that can take 30-60 seconds
          if (retryCount < 20) {
            setTimeout(() => {
              fetchedKeysRef.current.delete(attemptKey);  // Allow retry
              fetchMissingTranslation(key, language, ns, retryCount + 1);
            }, (data.estimated_time || 3) * 1000);
          }
          return;
        }

        // Translation found or completed
        if (data.success && data.value && data.value !== key) {
          // console.log(`✅ Translation loaded for "${key}" (${language}): ${data.value}`);

          // Add the translation to i18next
          const keyParts = key.split('.');
          if (keyParts.length === 1) {
            i18n.addResource(language, ns, key, data.value);
          } else {
            // Nested key - build nested object
            const nestedObj = {};
            let current = nestedObj;
            for (let i = 0; i < keyParts.length - 1; i++) {
              current[keyParts[i]] = {};
              current = current[keyParts[i]];
            }
            current[keyParts[keyParts.length - 1]] = data.value;

            // Merge with existing translations
            const existingBundle = i18n.getResourceBundle(language, ns) || {};
            const merged = deepMerge(existingBundle, nestedObj);
            i18n.addResourceBundle(language, ns, merged, true, true);
          }

          // Force re-render by incrementing version
          setTranslationVersion(v => v + 1);
        }
      }
    } catch (error) {
      // console.warn(`Failed to fetch missing translation for "${key}":`, error);
    }
  }, [i18n, setTranslationVersion]);

  // NOTE: Event listener for 'translationMissing' REMOVED!
  // Individual translation fetches are disabled.
  // Use useBatchTranslation() for loading translations instead.

  // Enhanced t function with fallback handling AND inline editing support
  const t = useCallback((key, options = {}) => {
    try {
      // Force re-evaluation by depending on translationVersion
      const _ = translationVersion;

      const translated = i18nT(key, options);

      // NOTE: Automatic individual fetch REMOVED!
      // Use useBatchTranslation() for batch loading instead.
      // Individual fetches cause 200+ redundant requests and are disabled.

      // Check if this is a loading state (starts with ⟳)
      const isLoading = typeof translated === 'string' && translated.startsWith('⟳');

      // 🔥 FIX: Show loading spinner when translation key = value AND bundle is completely empty
      // If bundle has ANY content, translations have loaded - missing keys just don't exist in DB
      const isKeyNotTranslated = typeof translated === 'string' && translated === key && key.includes('.');

      // Check if the i18n bundle is completely empty (not loaded at all)
      // Note: bundle may have nested structure, so even 1 top-level key means translations loaded
      const bundle = i18n.getResourceBundle(i18n.language, namespace);
      const isBundleCompletelyEmpty = !bundle || Object.keys(bundle).length === 0;
      const shouldShowLoadingForMissingTranslation = isKeyNotTranslated && isBundleCompletelyEmpty;


      // In browser environment, wrap with inline editor for admins or show spinner for loading
      if (typeof window !== 'undefined' && options?.editable !== false) {
        // Lazy load TranslationEdit to avoid SSR issues
        try {
          const TranslationEdit = require('@/components/TranslationEditWrapper').TranslationEdit;

          // If loading OR provider not ready with untranslated key, show spinner
          if (isLoading || shouldShowLoadingForMissingTranslation) {
            const loadingText = isLoading ? translated.replace('⟳ ', '') : '';
            return (
              <span className="inline-flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" aria-label="Loading"></span>
                {loadingText && <span className="text-sm">{loadingText}</span>}
              </span>
            );
          }

          return (
            <TranslationEdit
              key={`${key}-${i18n.language}`}
              translationKey={key}
              language={i18n.language}
              namespace={namespace}
            >
              {translated}
            </TranslationEdit>
          );
        } catch (err) {
          // If TranslationEdit fails to load, return plain text (with spinner if loading)
          if (isLoading || shouldShowLoadingForMissingTranslation) {
            return '...'; // Fallback to simple loading indicator
          }
          return translated;
        }
      }

      // SSR or editable=false: show loading indicator for untranslated keys during initial load
      if (shouldShowLoadingForMissingTranslation) {
        return '...';
      }

      return translated;
    } catch (error) {
      // console.warn(`Translation error for key "${key}":`, error);
      return key;
    }
  }, [i18nT, i18n.language, isBackendAvailable, namespace, translationVersion, isProviderReady]);

  return {
    t,
    i18n,
    translateDynamic,
    isBackendAvailable,
    ...rest
  };
}

/**
 * Hook for typed translations with predefined categories
 */
export function useTypedTranslation() {
  const { t } = useTranslation();

  return {
    // Common UI elements
    tButton: useCallback((key, options) => t(`common.buttons.${key}`, options), [t]),
    tLabel: useCallback((key, options) => t(`common.labels.${key}`, options), [t]),
    tMessage: useCallback((key, options) => t(`common.messages.${key}`, options), [t]),
    
    // Navigation
    tNav: useCallback((key, options) => t(`navigation.${key}`, options), [t]),
    
    // Pages
    tPage: useCallback((key, options) => t(`pages.${key}`, options), [t]),
    
    // Errors
    tError: useCallback((key, options) => t(`errors.${key}`, options), [t]),
    
    // Forms
    tForm: useCallback((key, options) => t(`forms.${key}`, options), [t]),
  };
}

/**
 * Hook for dynamic content translation (user-generated, DB content)
 */
export function useDynamicTranslation() {
  const { translateDynamic, language } = useEnhancedLanguage();
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationCache] = useState(new Map());

  const translateContent = useCallback(async (text, options = {}) => {
    const {
      targetLanguage = language.code,
      sourceLanguage = 'de',
      context = null,
      useCache = true
    } = options;

    // Return original if same language
    if (sourceLanguage === targetLanguage) {
      return text;
    }

    // Check cache first
    const cacheKey = `${text}_${sourceLanguage}_${targetLanguage}`;
    if (useCache && translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey);
    }

    setIsTranslating(true);
    try {
      const translated = await translateDynamic(text, targetLanguage, context);
      
      // Cache the result
      if (useCache) {
        translationCache.set(cacheKey, translated);
      }
      
      return translated;
    } catch (error) {
      console.error('Dynamic translation failed:', error);
      return text; // Return original on error
    } finally {
      setIsTranslating(false);
    }
  }, [translateDynamic, language.code, translationCache]);

  return {
    translateContent,
    isTranslating,
    clearCache: useCallback(() => translationCache.clear(), [translationCache])
  };
}

/**
 * Hook for database field translations (status, categories, etc.)
 */
export function useDbFieldTranslation() {
  const { language } = useEnhancedLanguage();
  const [isTranslating, setIsTranslating] = useState(false);
  const [fieldCache] = useState(new Map());

  const translateField = useCallback(async (tableName, fieldName, fieldValue, options = {}) => {
    const {
      targetLanguage = language.code,
      useCache = true,
      organizationId = null
    } = options;

    // Check cache first
    const cacheKey = `${tableName}_${fieldName}_${fieldValue}_${targetLanguage}_${organizationId}`;
    if (useCache && fieldCache.has(cacheKey)) {
      return fieldCache.get(cacheKey);
    }

    setIsTranslating(true);
    try {
      const response = await fetch('/api/translations/db-field', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table_name: tableName,
          field_name: fieldName,
          field_value: fieldValue,
          target_language: targetLanguage,
          organization_id: organizationId
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const translated = data.translated_value;
          
          // Cache the result
          if (useCache) {
            fieldCache.set(cacheKey, translated);
          }
          
          return translated;
        }
      }
      
      // Return original value on error
      return fieldValue;
      
    } catch (error) {
      console.error('DB field translation failed:', error);
      return fieldValue;
    } finally {
      setIsTranslating(false);
    }
  }, [language.code, fieldCache]);

  return {
    translateField,
    isTranslating,
    clearCache: useCallback(() => fieldCache.clear(), [fieldCache])
  };
}

/**
 * Hook for batch translations (efficient for multiple keys)
 */
export function useBatchTranslation() {
  const { language } = useEnhancedLanguage();
  const { i18n } = useI18nextTranslation();
  const [isLoading, setIsLoading] = useState(true); // Start true until first load completes
  const [hasInitialized, setHasInitialized] = useState(false);
  const loadedKeysRef = useRef(new Set());

  const translateBatch = useCallback(async (keys, options = {}) => {
    const {
      targetLanguage = language.code,
      namespace = 'default',
      organizationId = null
    } = options;

    setIsLoading(true);
    try {
      const response = await fetch('/api/translations/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keys,
          language: targetLanguage,
          namespace,
          organization_id: organizationId
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          return data.translations;
        }
      }

      // Return keys as fallback
      return keys.reduce((acc, key) => {
        acc[key] = key;
        return acc;
      }, {});

    } catch (error) {
      console.error('Batch translation failed:', error);
      return keys.reduce((acc, key) => {
        acc[key] = key;
        return acc;
      }, {});
    } finally {
      setIsLoading(false);
    }
  }, [language.code]);

  /**
   * Load translations into i18next resource bundle
   * This preloads all keys so t() returns immediately without loading state
   */
  const loadTranslations = useCallback(async (keys, options = {}) => {
    const {
      namespace = 'translation',
      organizationId = 1
    } = options;

    const lang = language?.code || 'de';

    // Filter out already loaded keys
    const keysToLoad = keys.filter(key => !loadedKeysRef.current.has(`${lang}:${key}`));

    if (keysToLoad.length === 0) {
      return; // All keys already loaded
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/translations/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keys: keysToLoad,
          language: lang,
          namespace: 'default',
          organization_id: organizationId
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.translations) {
          // Mark keys as batch-loaded to prevent individual fetches
          if (typeof window !== 'undefined') {
            if (!window.__batchLoadedTranslationKeys) {
              window.__batchLoadedTranslationKeys = new Set();
            }
            keysToLoad.forEach(key => window.__batchLoadedTranslationKeys.add(key));
          }

          // Add each translation to i18next
          Object.entries(data.translations).forEach(([key, value]) => {
            if (value && value !== key) {
              // Build nested object for dotted keys
              const keyParts = key.split('.');
              if (keyParts.length === 1) {
                i18n.addResource(lang, namespace, key, value);
              } else {
                const nestedObj = {};
                let current = nestedObj;
                for (let i = 0; i < keyParts.length - 1; i++) {
                  current[keyParts[i]] = {};
                  current = current[keyParts[i]];
                }
                current[keyParts[keyParts.length - 1]] = value;

                const existingBundle = i18n.getResourceBundle(lang, namespace) || {};
                const merged = deepMerge(existingBundle, nestedObj);
                i18n.addResourceBundle(lang, namespace, merged, true, true);
              }

              // Mark as loaded
              loadedKeysRef.current.add(`${lang}:${key}`);
            }
          });
        }
      }
    } catch (error) {
      console.error('Load translations failed:', error);
    } finally {
      setIsLoading(false);
      setHasInitialized(true);
    }
  }, [language?.code, i18n]);

  // Set loading to false if no loadTranslations is called after mount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasInitialized) {
        setIsLoading(false);
        setHasInitialized(true);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [hasInitialized]);

  return {
    translateBatch,
    loadTranslations,
    isLoading: isLoading && !hasInitialized // Only loading before first initialization
  };
}

/**
 * Hook for translation management (admin features)
 */
export function useTranslationAdmin() {
  const { language } = useEnhancedLanguage();

  const createTranslation = useCallback(async (key, value, options = {}) => {
    const {
      targetLanguage = language.code,
      namespace = 'default',
      context = null,
      organizationId = null
    } = options;

    try {
      const response = await fetch('/api/translations/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key,
          language: targetLanguage,
          value,
          namespace,
          context,
          organization_id: organizationId
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Create translation failed:', error);
      return false;
    }
  }, [language.code]);

  const getTranslationStats = useCallback(async (targetLanguage = language.code, organizationId = null) => {
    try {
      const response = await fetch(`/api/translations/stats/${targetLanguage}?organization_id=${organizationId}`);
      
      if (response.ok) {
        return await response.json();
      }
      
      return null;
    } catch (error) {
      console.error('Get translation stats failed:', error);
      return null;
    }
  }, [language.code]);

  const startAutoTranslation = useCallback(async (targetLanguage, options = {}) => {
    const {
      sourceLanguage = 'de',
      batchSize = 50,
      organizationId = null
    } = options;

    try {
      const response = await fetch('/api/translations/jobs/auto-translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_language: targetLanguage,
          source_language: sourceLanguage,
          batch_size: batchSize,
          organization_id: organizationId
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }
      
      return null;
    } catch (error) {
      console.error('Start auto-translation failed:', error);
      return null;
    }
  }, []);

  return {
    createTranslation,
    getTranslationStats,
    startAutoTranslation
  };
}
